import { useEffect, useRef, useState } from 'react';
import { FileIcon } from './FileIcon.tsx';
import { formatBytes, formatRelative } from '../../lib/format.ts';
import { IconLock, IconMore, IconShare } from '../../lib/icons.tsx';
import type { ApiNode } from '../../api/types.ts';

export interface RowHandlers {
  onOpen: (node: ApiNode) => void;
  onSelect: (node: ApiNode, event: React.MouseEvent) => void;
  onContextMenu: (node: ApiNode, event: React.MouseEvent) => void;
  onRename: (node: ApiNode, nextName: string) => void;
  onDropInto: (targetFolder: ApiNode, draggedPaths: string[]) => void;
  onDragStartPaths: (node: ApiNode) => string[];
}

interface Props {
  node: ApiNode;
  selected: boolean;
  renaming: boolean;
  onCancelRename: () => void;
  handlers: RowHandlers;
  style?: React.CSSProperties;
}

export function FileRow({
  node, selected, renaming, onCancelRename, handlers, style,
}: Props) {
  const [dropActive, setDropActive] = useState(false);
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!renaming) return;
    const input = inputRef.current;
    if (!input) return;
    input.focus();
    // 확장자는 빼고 이름 부분만 선택해 준다
    const dot = node.name.lastIndexOf('.');
    input.setSelectionRange(0, dot > 0 ? dot : node.name.length);
  }, [renaming, node.name]);

  const commitRename = (): void => {
    const next = inputRef.current?.value.trim() ?? '';
    if (next && next !== node.name) handlers.onRename(node, next);
    else onCancelRename();
  };

  return (
    <div
      className="frow"
      role="row"
      aria-selected={selected}
      data-selected={selected}
      data-drop={dropActive}
      data-dragging={dragging}
      style={style}
      draggable={!renaming}
      onClick={(e) => { handlers.onSelect(node, e); }}
      onDoubleClick={() => { if (!renaming) handlers.onOpen(node); }}
      onContextMenu={(e) => { handlers.onContextMenu(node, e); }}
      onDragStart={(e) => {
        setDragging(true);
        const paths = handlers.onDragStartPaths(node);
        e.dataTransfer.setData('application/x-yumsync-paths', JSON.stringify(paths));
        e.dataTransfer.effectAllowed = 'move';
      }}
      onDragEnd={() => { setDragging(false); }}
      onDragOver={(e) => {
        if (node.type !== 'folder') return;
        if (!e.dataTransfer.types.includes('application/x-yumsync-paths')) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        setDropActive(true);
      }}
      onDragLeave={() => { setDropActive(false); }}
      onDrop={(e) => {
        setDropActive(false);
        if (node.type !== 'folder') return;
        const raw = e.dataTransfer.getData('application/x-yumsync-paths');
        if (!raw) return;
        e.preventDefault();
        e.stopPropagation();
        handlers.onDropInto(node, JSON.parse(raw) as string[]);
      }}
    >
      <input
        className="checkbox"
        type="checkbox"
        checked={selected}
        onClick={(e) => { e.stopPropagation(); }}
        onChange={(e) => {
          handlers.onSelect(node, {
            ...e, metaKey: true, ctrlKey: true, shiftKey: false,
          } as unknown as React.MouseEvent);
        }}
        aria-label={`${node.name} 선택`}
      />

      <div className="frow__name">
        <FileIcon node={node} />
        {renaming ? (
          <input
            ref={inputRef}
            className="rename"
            defaultValue={node.name}
            onClick={(e) => { e.stopPropagation(); }}
            onBlur={commitRename}
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
              if (e.key === 'Escape') onCancelRename();
            }}
          />
        ) : (
          <span className="frow__label" title={node.name}>{node.name}</span>
        )}
        {node.lock && <IconLock size={14} className="faint" />}
        {node.is_shared && <IconShare size={14} className="faint" />}
      </div>

      <div className="frow__meta">{node.type === 'folder' ? '—' : formatBytes(node.size)}</div>
      <div className="frow__meta" title={node.modified_at}>
        {formatRelative(node.modified_at)}
        {node.modified_by && <span className="faint"> · {node.modified_by.name}</span>}
      </div>

      <button
        className="btn btn--ghost btn--icon btn--sm"
        aria-label={`${node.name} 메뉴`}
        onClick={(e) => { e.stopPropagation(); handlers.onContextMenu(node, e); }}
      >
        <IconMore size={16} />
      </button>
    </div>
  );
}
